import { createContext, useContext, useCallback, useMemo, useRef, useState } from 'react';

const UploadProgressContext = createContext(null);

export function UploadProgressProvider({ children }) {
  const [jobs, setJobs] = useState(new Map());
  const counterRef = useRef(0);

  const addJob = useCallback(() => {
    const id = `upload-${++counterRef.current}`;
    setJobs(prev => {
      const next = new Map(prev);
      next.set(id, {
        id, open: false, phase: 'uploading', percent: 0,
        processed: undefined, total: undefined,
        title: '', subtitle: '', errorMessage: '', errorDetail: '',
        cancelCallback: null,
      });
      return next;
    });
    return id;
  }, []);

  const updateJob = useCallback((id, patch) => {
    setJobs(prev => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.set(id, { ...prev.get(id), ...patch });
      return next;
    });
  }, []);

  const removeJob = useCallback((id) => {
    setJobs(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const cancelJob = useCallback((id) => {
    setJobs(prev => {
      const job = prev.get(id);
      if (job?.cancelCallback) {
        try { job.cancelCallback(); } catch {}
      }
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const hasActiveJobs = useMemo(
    () => [...jobs.values()].some(j => j.open && j.phase !== 'error'),
    [jobs]
  );

  return (
    <UploadProgressContext.Provider value={{ jobs, addJob, updateJob, removeJob, cancelJob, hasActiveJobs }}>
      {children}
    </UploadProgressContext.Provider>
  );
}

export function useUploadProgressContext() {
  return useContext(UploadProgressContext);
}
